import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useGroup } from '../../context/GroupContext';
import groupService from '../../api/groupService';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons';

const AddPrayerNameModal = ({ editingName, onClose, onSave }) => {
  const { currentUser } = useAuth();
  const { adminGroup } = useGroup();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  const [formData, setFormData] = useState({
    recipient_name: '',
    intention_type: 'Refuah',
    custom_intention: ''
  });

  useEffect(() => {
    if (editingName) {
      setFormData({
        recipient_name: editingName.recipient_name || '',
        intention_type: editingName.intention_type || 'Refuah',
        custom_intention: editingName.custom_intention || ''
      });
    }
  }, [editingName]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.recipient_name.trim()) {
      setError('Name is required');
      return;
    }

    if (!adminGroup?.id) {
      setError('No community selected');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      const data = {
        community_id: adminGroup.id,
        recipient_name: formData.recipient_name.trim(),
        intention_type: formData.intention_type === 'Custom' ? null : formData.intention_type,
        custom_intention: formData.intention_type === 'Custom' ? formData.custom_intention.trim() : null,
        added_by_user_id: currentUser?.id
      };

      let result;
      if (editingName) {
        result = await groupService.updatePrayerName(editingName.id, data);
      } else {
        result = await groupService.createPrayerName(data);
      }
      
      onSave(result.data);
    } catch (err) {
      console.error('Error saving prayer name:', err);
      setError(err.response?.data?.error || 'Failed to save prayer name');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">
            {editingName ? 'Edit Prayer Name' : 'Add Prayer Name'}
          </h2>
          <button 
            className="text-gray-400 hover:text-gray-600 transition-colors"
            onClick={onClose}
          >
            <FontAwesomeIcon icon={faTimes} className="text-xl" />
          </button>
        </div>

        {/* Error Alert */}
        {error && (
          <div className="mx-6 mt-4 px-4 py-3 bg-red-50 border border-red-200 rounded-md">
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="px-6 py-4">
          {/* Name Field */}
          <div className="mb-4">
            <label htmlFor="recipient_name" className="block text-sm font-medium text-gray-700 mb-1">
              Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="recipient_name"
              name="recipient_name"
              value={formData.recipient_name}
              onChange={handleChange}
              placeholder="Avraham ben Avraham"
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* Intention Type Field */}
          <div className="mb-4">
            <label htmlFor="intention_type" className="block text-sm font-medium text-gray-700 mb-1">
              Intention <span className="text-red-500">*</span>
            </label>
            <select
              id="intention_type"
              name="intention_type"
              value={formData.intention_type}
              onChange={handleChange}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="Refuah">Refuah (Healing)</option>
              <option value="Memory">Memory (In Memory Of)</option>
              <option value="Health">Health</option>
              <option value="Success">Success</option>
              <option value="Zivug">Zivug (Finding Match)</option>
              <option value="Custom">Custom</option>
            </select>
          </div>

          {/* Custom Intention Field (conditional) */}
          {formData.intention_type === 'Custom' && (
            <div className="mb-4">
              <label htmlFor="custom_intention" className="block text-sm font-medium text-gray-700 mb-1">
                Custom Intention <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="custom_intention"
                name="custom_intention"
                value={formData.custom_intention}
                onChange={handleChange}
                placeholder="Enter custom prayer intention"
                required={formData.intention_type === 'Custom'}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          )}

          {/* Footer Buttons */}
          <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200 mt-6">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Saving...' : editingName ? 'Update Name' : 'Add Name'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddPrayerNameModal;
